import React, { useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";

import { SectionCard, StatusPill } from "components/admin";
import { SchoolPlayerProfileTabProps } from "pages/app/workspace/school-player-profile/school-player-profile.types";
import {
  SchoolPlayerProfileBadgeRow,
  SchoolPlayerProfileBioText,
  SchoolPlayerProfileChipRow,
  SchoolPlayerProfileContactLabel,
  SchoolPlayerProfileContactList,
  SchoolPlayerProfileContactRow,
  SchoolPlayerProfileContactValue,
  SchoolPlayerProfileEmptyCopy,
  SchoolPlayerProfileEyebrowText,
  SchoolPlayerProfileFootprintBlock,
  SchoolPlayerProfileFootprintHeading,
  SchoolPlayerProfileFootprintStack,
  SchoolPlayerProfileGuardianCard,
  SchoolPlayerProfileGuardianHeader,
  SchoolPlayerProfileGuardianName,
  SchoolPlayerProfileHeroBody,
  SchoolPlayerProfileHeroEyebrow,
  SchoolPlayerProfileHeroImage,
  SchoolPlayerProfileHeroMeta,
  SchoolPlayerProfileHeroPanel,
  SchoolPlayerProfileHeroTextGroup,
  SchoolPlayerProfileHeroTitle,
  SchoolPlayerProfileInfoRow,
  SchoolPlayerProfileLabelText,
  SchoolPlayerProfileMetaTextSecondary,
  SchoolPlayerProfileMetadataGrid,
  SchoolPlayerProfileMetadataTile,
  SchoolPlayerProfileStatusBadge,
  SchoolPlayerProfileTabStack,
  SchoolPlayerProfileTeamButton,
  SchoolPlayerProfileTeamList,
  SchoolPlayerProfileTextGroup,
  SchoolPlayerProfileThumbnailImage,
  SchoolPlayerProfileTitleText,
  SchoolPlayerProfileTopGrid,
} from "pages/app/workspace/styles/school-player-profile.page.inline.styles";
import { formatDate, formatDateTime } from "utils/admin";
import { formatPortalLabel } from "utils/portal-access";

export const SchoolPlayerOverviewTab: React.FC<SchoolPlayerProfileTabProps> = ({
  athlete,
  linkedSports,
  linkedTeams,
  schoolId,
  schoolPlayer,
}) => {
  const history = useHistory();
  const location = useLocation();
  const profileTitle = schoolPlayer?.full_name || athlete?.full_name || "School player";
  const position = schoolPlayer?.position || athlete?.position || "Position unknown";
  const grade = schoolPlayer?.school_grade || null;
  const schoolName =
    schoolPlayer?.school_name || athlete?.school_summary?.school_name || "Unknown school";
  const bio = athlete?.profile?.bio || null;
  const profileBackTo = `${location.pathname}${location.search}`;

  const heroMeta = useMemo(
    () => [grade ? `Grade ${grade}` : null, schoolName].filter(Boolean).join(" · "),
    [grade, schoolName],
  );

  const identityMeta = useMemo(
    () =>
      [
        ["Date of birth", formatDate(schoolPlayer?.date_of_birth)],
        ["Gender", formatPortalLabel(schoolPlayer?.gender || "Unknown")],
        ["Grade", grade || "Not set"],
      ] as Array<[string, string]>,
    [grade, schoolPlayer?.date_of_birth, schoolPlayer?.gender],
  );

  const guardianContacts = useMemo(
    () =>
      [
        ["Email", schoolPlayer?.guardian_email || "Not provided"],
        ["Phone", schoolPlayer?.guardian_phone || "Not provided"],
        ["Consented", formatDateTime(schoolPlayer?.consented_at)],
      ] as Array<[string, string]>,
    [schoolPlayer?.consented_at, schoolPlayer?.guardian_email, schoolPlayer?.guardian_phone],
  );

  const openTeam = (teamId: string) => {
    if (!schoolId || !teamId) {
      return;
    }

    history.push({
      pathname: `/workspace/school-teams/${schoolId}/${teamId}`,
      state: { backTo: profileBackTo },
    });
  };

  return (
    <SchoolPlayerProfileTabStack>
      <SchoolPlayerProfileTopGrid>
        <SectionCard title="Player profile">
          <SchoolPlayerProfileHeroPanel>
            <SchoolPlayerProfileHeroImage
              alt={profileTitle}
              fallbackLabel={profileTitle}
              src={
                schoolPlayer?.avatar_url ||
                schoolPlayer?.image_url ||
                schoolPlayer?.image ||
                athlete?.avatar_url ||
                athlete?.image_url ||
                athlete?.image
              }
              variant="athlete"
            />
            <SchoolPlayerProfileHeroBody>
              <SchoolPlayerProfileHeroTextGroup>
                <SchoolPlayerProfileHeroEyebrow>{position}</SchoolPlayerProfileHeroEyebrow>
                <SchoolPlayerProfileHeroTitle>{profileTitle}</SchoolPlayerProfileHeroTitle>
                <SchoolPlayerProfileHeroMeta>{heroMeta}</SchoolPlayerProfileHeroMeta>
                <SchoolPlayerProfileBadgeRow>
                  <StatusPill
                    value={formatPortalLabel(schoolPlayer?.profile_visibility || "restricted")}
                  />
                  {linkedTeams.length > 0 ? (
                    <span className="inline-flex items-center rounded-full border border-[var(--brand-border)] bg-[rgba(255,255,255,0.04)] px-3 py-1 text-xs font-medium text-[var(--brand-muted)]">
                      {linkedTeams.length} {linkedTeams.length === 1 ? "team" : "teams"}
                    </span>
                  ) : null}
                </SchoolPlayerProfileBadgeRow>
                {bio ? <SchoolPlayerProfileBioText>{bio}</SchoolPlayerProfileBioText> : null}
              </SchoolPlayerProfileHeroTextGroup>

              <SchoolPlayerProfileMetadataGrid>
                {identityMeta.map(([label, value]) => (
                  <SchoolPlayerProfileMetadataTile key={label}>
                    <SchoolPlayerProfileEyebrowText>{label}</SchoolPlayerProfileEyebrowText>
                    <SchoolPlayerProfileTitleText>{value}</SchoolPlayerProfileTitleText>
                  </SchoolPlayerProfileMetadataTile>
                ))}
              </SchoolPlayerProfileMetadataGrid>
            </SchoolPlayerProfileHeroBody>
          </SchoolPlayerProfileHeroPanel>
        </SectionCard>

        <SectionCard className="flex h-full flex-col" title="Guardian & Consent">
          <SchoolPlayerProfileGuardianCard>
            <SchoolPlayerProfileGuardianHeader>
              <SchoolPlayerProfileGuardianName>
                {schoolPlayer?.guardian_name || "Guardian not provided"}
              </SchoolPlayerProfileGuardianName>
              <StatusPill value={schoolPlayer?.consent_status || "pending"} />
            </SchoolPlayerProfileGuardianHeader>

            <SchoolPlayerProfileContactList>
              {guardianContacts.map(([label, value]) => (
                <SchoolPlayerProfileContactRow key={label}>
                  <SchoolPlayerProfileContactLabel>{label}</SchoolPlayerProfileContactLabel>
                  <SchoolPlayerProfileContactValue>{value}</SchoolPlayerProfileContactValue>
                </SchoolPlayerProfileContactRow>
              ))}
            </SchoolPlayerProfileContactList>
          </SchoolPlayerProfileGuardianCard>
        </SectionCard>
      </SchoolPlayerProfileTopGrid>

      <SectionCard title="School Footprint">
        <SchoolPlayerProfileFootprintStack>
          <SchoolPlayerProfileFootprintBlock>
            <SchoolPlayerProfileFootprintHeading>Sports</SchoolPlayerProfileFootprintHeading>
            {linkedSports.length > 0 ? (
              <SchoolPlayerProfileChipRow>
                {linkedSports.map((sport: any) => (
                  <SchoolPlayerProfileStatusBadge key={String(sport.id || sport.name)}>
                    {sport.name || "Unknown"}
                  </SchoolPlayerProfileStatusBadge>
                ))}
              </SchoolPlayerProfileChipRow>
            ) : (
              <SchoolPlayerProfileEmptyCopy>No sports registered yet.</SchoolPlayerProfileEmptyCopy>
            )}
          </SchoolPlayerProfileFootprintBlock>

          <SchoolPlayerProfileFootprintBlock>
            <SchoolPlayerProfileFootprintHeading>Linked teams</SchoolPlayerProfileFootprintHeading>
            {linkedTeams.length > 0 ? (
              <SchoolPlayerProfileTeamList>
                {linkedTeams.map((team: any) => {
                  const teamId = String(team.id || "");
                  const canOpen = Boolean(schoolId && teamId);

                  return (
                    <SchoolPlayerProfileTeamButton
                      disabled={!canOpen}
                      key={teamId || team.name}
                      onClick={() => openTeam(teamId)}
                      type="button"
                    >
                      <SchoolPlayerProfileInfoRow>
                        <SchoolPlayerProfileThumbnailImage
                          alt={team.name || "Team"}
                          fallbackLabel={team.name}
                          src={team.image_url}
                          variant="team"
                        />
                        <SchoolPlayerProfileTextGroup>
                          <SchoolPlayerProfileLabelText>
                            {team.name || "Unknown"}
                          </SchoolPlayerProfileLabelText>
                          <SchoolPlayerProfileMetaTextSecondary>
                            {team.sport_name || "School team"}
                          </SchoolPlayerProfileMetaTextSecondary>
                        </SchoolPlayerProfileTextGroup>
                      </SchoolPlayerProfileInfoRow>
                    </SchoolPlayerProfileTeamButton>
                  );
                })}
              </SchoolPlayerProfileTeamList>
            ) : (
              <SchoolPlayerProfileEmptyCopy>No linked teams yet.</SchoolPlayerProfileEmptyCopy>
            )}
          </SchoolPlayerProfileFootprintBlock>
        </SchoolPlayerProfileFootprintStack>
      </SectionCard>
    </SchoolPlayerProfileTabStack>
  );
};
